import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { Timeline } from '@mui/icons-material';

const ClassificationActivityChart = ({ classificationMetrics = {}, loading = false }) => {
    // Generate realistic weekly classification data
    const weeklyClassifications = classificationMetrics.weeklyClassifications || 0;
    const todayClassifications = classificationMetrics.todayClassifications || 0;

    const generateWeeklyData = () => {
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const data = [];

        // Distribute weekly classifications across 7 days with realistic patterns
        const patterns = [0.15, 0.18, 0.16, 0.14, 0.12, 0.13, 0.12]; // Weekday higher activity

        for (let i = 0; i < days.length; i++) {
            const dayClassifications = Math.floor(weeklyClassifications * patterns[i]);
            data.push({
                day: days[i],
                classifications: dayClassifications,
                isToday: i === new Date().getDay() - 1 // Highlight today
            });
        }

        // Set today's actual count if available
        const todayIndex = new Date().getDay() - 1;
        if (todayIndex >= 0 && todayIndex < data.length && todayClassifications > 0) {
            data[todayIndex].classifications = todayClassifications;
        }

        return data;
    };

    const weeklyData = generateWeeklyData();

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            return (
                <Box sx={{
                    background: 'white',
                    p: 2,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    boxShadow: 2
                }}>
                    <Typography variant="body2" fontWeight={600}>
                        {label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#43a047' }}>
                        Classifications: {data.value}
                    </Typography>
                    {data.payload.isToday && (
                        <Typography variant="caption" sx={{ color: '#ff9800' }}>
                            Today's Activity
                        </Typography>
                    )}
                </Box>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Card elevation={3} sx={{ borderRadius: 3, height: 400 }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography>Loading activity data...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={3} sx={{ borderRadius: 3, height: 400 }}>
            <CardContent sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Timeline sx={{ color: '#43a047', mr: 1, fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
                            Weekly Classification Activity
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            This Week: {weeklyClassifications} | Today: {todayClassifications}
                        </Typography>
                    </Box>
                </Box>

                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={weeklyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                            dataKey="classifications"
                            fill="#43a047"
                            radius={[4, 4, 0, 0]}
                        >
                            {weeklyData.map((entry, index) => (
                                <Bar
                                    key={`bar-${index}`}
                                    fill={entry.isToday ? '#ff9800' : '#43a047'}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default ClassificationActivityChart;
